import * as React from 'react';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Insurance } from '@/api';
import type { Car } from '@/api/cars/types';
import { formatNumber, getDailyInsuranceCost } from '@/core';
import { Card, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Cancel } from '@/ui/icons/cancel';
import { Checkmark } from '@/ui/icons/checkmark';
import { Exclamations } from '@/ui/icons/exclamations';
import { ShieldCheckmark } from '@/ui/icons/shield-checkmark';
import { ShieldSlash } from '@/ui/icons/shield-slash';

interface InsuranceCardItemProps {
  insurance: Insurance;
  style?: any;
  disabled?: boolean;
  active?: boolean;
  onPress?: () => void;
  car?: Car;
}

export const InsuranceCardItem = ({
  insurance,
  style,
  disabled = false,
  active = false,
  onPress,
  car,
}: InsuranceCardItemProps) => {
  const { styles, theme } = useStyles(stylesheet);
  const [hover, setHover] = React.useState(false);

  const getStyle = () => {
    const showDisabled = disabled;
    let stateStyle = styles.default;

    if (showDisabled) {
      stateStyle = styles.disabled;
    }
    if (hover) {
      stateStyle = styles.hover;
    }
    if (active) {
      stateStyle = styles.active;
    }

    return [stateStyle];
  };

  return (
    <Pressable
      style={[styles.container, style]}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      hitSlop={8}
      onPress={() => onPress?.()}
    >
      <Card style={[styles.card, getStyle()]}>
        <View style={styles.contentContainer}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <View style={{ padding: 2 }}>
              {(insurance.name || insurance.category) === 'No Protection' ? (
                <Icon
                  type="dressed"
                  icon={<ShieldSlash />}
                  fillColor={
                    active
                      ? theme.colors.chip.brand.color
                      : theme.colors.icon.color
                  }
                  width={25}
                  height={25}
                />
              ) : (
                <Icon
                  type="dressed"
                  icon={<ShieldCheckmark />}
                  fillColor={
                    active
                      ? theme.colors.chip.brand.color
                      : theme.colors.icon.color
                  }
                  width={25}
                  height={25}
                />
              )}
            </View>

            <Text type="bodyBold">{insurance.name || insurance.category}</Text>
          </View>

          <View
            style={{
              paddingRight: 16,
              paddingLeft: 8,
              paddingTop: 8,
              gap: 8,
            }}
          >
            {insurance.meta.covered &&
              insurance.meta.covered.map(
                (description: string, index: number) => (
                  <View
                    key={index}
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                    }}
                  >
                    <View style={{ padding: 2 }}>
                      <Icon
                        type="naked"
                        icon={<Checkmark />}
                        fillColor={theme.colors.input.success.helperText}
                        stroke={theme.colors.input.success.helperText}
                        width={12}
                        height={12}
                        iconStyle={{
                          minWidth: 12,
                          minHeight: 12,
                        }}
                      />
                    </View>

                    <Text
                      style={{
                        color: theme.colors.text.secondary,
                        flex: 1,
                      }}
                      type="bodyRegular"
                    >
                      {description}
                    </Text>
                  </View>
                )
              )}

            {insurance.meta.notCovered &&
              insurance.meta.notCovered.map(
                (description: string, index: number) => (
                  <View
                    key={index}
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                    }}
                  >
                    <View style={{ padding: 2 }}>
                      <Icon
                        type="naked"
                        icon={<Cancel />}
                        fillColor={theme.colors.icon.color}
                        stroke={theme.colors.icon.color}
                        width={12}
                        height={12}
                        iconStyle={{
                          minWidth: 12,
                          minHeight: 12,
                        }}
                      />
                    </View>

                    <Text
                      style={{
                        color: theme.colors.text.secondary,
                        flex: 1,
                      }}
                      type="bodyRegular"
                    >
                      {description}
                    </Text>
                  </View>
                )
              )}

            {insurance.meta.warning &&
              insurance.meta.warning.map(
                (description: string, index: number) => (
                  <View
                    key={index}
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                    }}
                  >
                    <View style={{ padding: 2 }}>
                      <Icon
                        type="naked"
                        icon={<Exclamations />}
                        fillColor={theme.colors.icon.color}
                        width={12}
                        height={12}
                      />
                    </View>

                    <Text
                      style={{
                        color: theme.colors.text.secondary,
                        flex: 1,
                      }}
                      type="bodyRegular"
                    >
                      {description}
                    </Text>
                  </View>
                )
              )}

            {insurance.meta.descriptions &&
              insurance.meta.descriptions.map(
                (description: string, index: number) => (
                  <View
                    key={index}
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                    }}
                  >
                    <Text
                      style={{
                        color: theme.colors.text.secondary,
                        flex: 1,
                      }}
                      type="bodyRegular"
                    >
                      {description}
                    </Text>
                  </View>
                )
              )}
          </View>
        </View>

        <View style={styles.pricingContainer}>
          {car ? (
            <View
              style={{
                flexDirection: 'row',
                gap: 8,
              }}
            >
              <Text type="bodyBold">
                {(insurance.name || insurance.category) === 'No Protection'
                  ? 'Free'
                  : `+$${formatNumber(
                      getDailyInsuranceCost(insurance, car.dailyRate)
                    )}`}
              </Text>

              <Text
                type="caption1"
                style={{
                  color: theme.colors.text.secondary,
                  alignSelf: 'center',
                }}
              >
                {(insurance.name || insurance.category) !== 'No Protection' &&
                  `/ day`}
              </Text>
            </View>
          ) : null}
        </View>
      </Card>
    </Pressable>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    width: '100%',
    gap: 8,
  },
  card: {
    flexGrow: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: 16,
    gap: 16,
  },
  contentContainer: {
    gap: 8,
    width: '100%',
  },
  pricingContainer: {
    gap: 4,
  },
  default: {
    backgroundColor: theme.colors.card.default.backgroundColor,
    borderColor: theme.colors.card.default.borderColor,
  },
  active: {
    backgroundColor: theme.colors.card.active.backgroundColor,
    borderColor: theme.colors.card.active.borderColor,
    borderWidth: 1,
  },
  hover: {
    backgroundColor: theme.colors.card.hover.backgroundColor,
    borderColor: theme.colors.card.hover.borderColor,
  },
  disabled: {
    backgroundColor: theme.colors.card.disabled.backgroundColor,
    borderColor: theme.colors.card.disabled.borderColor,
  },
}));
